import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit, Eye, Building2, Mail, Phone, MapPin, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import api from '@/utils/api';
import useApiData from '@/hooks/useApiData';
import { toast } from 'sonner';
import { formatCurrency } from '@/utils/helpers';


const STATUS_CONFIG = {
  ativo: { label: 'Ativo', color: 'bg-green-100 text-green-700' },
  inativo: { label: 'Inativo', color: 'bg-slate-100 text-slate-700' },
  potencial: { label: 'Potencial', color: 'bg-blue-100 text-blue-700' },
  churned: { label: 'Churned', color: 'bg-red-100 text-red-700' },
};

const SEGMENTOS = ['Tecnologia', 'Saúde', 'Alimentação', 'Moda e Vestuário', 'Construção', 'Educação', 'Serviços', 'Varejo', 'Indústria', 'Outro'];
const ORIGENS = ['Google Ads', 'Instagram', 'Facebook', 'LinkedIn', 'Indicação', 'Site', 'Networking', 'Outro'];
const ESTADOS = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];

export default function Clientes() {
  const navigate = useNavigate();
  const { data: clientsData, refresh: refreshClients } = useApiData('/clients');
  const clients = clientsData || [];

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('geral');
  const [editingClient, setEditingClient] = useState(null);

  // Filtros derivados em tempo real (sem estado extra)
  const filteredClients = clients.filter(client => {
    const matchSearch = !searchTerm ||
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.contact?.email_principal?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.juridico?.razao_social?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.juridico?.cnpj?.includes(searchTerm);
    const matchStatus = statusFilter === 'todos' || client.status === statusFilter;
    return matchSearch && matchStatus;
  });



  const [formData, setFormData] = useState({
    name: '',
    status: 'ativo',
    // Contato
    contact: {
      email_principal: '',
      email_financeiro: '',
      phone_principal: '',
      phone_adicional: '',
      whatsapp: '',
      website: '',
      instagram: ''
    },
    // Jurídico
    juridico: {
      razao_social: '',
      nome_fantasia: '',
      cnpj: '',
      inscricao_estadual: '',
      inscricao_municipal: '',
      tax_regime: ''
    },
    // Financeiro
    financeiro: {
      dia_vencimento: 10,
      payment_method: '',
      ticket_medio: ''
    },
    // Endereço
    address: {
      zipcode: '',
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      country: 'Brasil'
    },
    // Estratégico
    origem: '',
    segmento: '',
    notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name?.trim()) {
      setActiveTab('geral');
      toast.error('O campo Nome/Razão Social é obrigatório');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        origem: formData.origem || null,
        segmento: formData.segmento || null,
        contact: {
          ...formData.contact,
          email_principal: formData.contact.email_principal || null,
          email_financeiro: formData.contact.email_financeiro || null
        },
        juridico: {
          ...formData.juridico,
          tax_regime: formData.juridico.tax_regime || null
        },
        financeiro: {
          ...formData.financeiro,
          dia_vencimento: parseInt(formData.financeiro.dia_vencimento) || 10,
          payment_method: formData.financeiro.payment_method || null,
          ticket_medio: formData.financeiro.ticket_medio ? parseFloat(formData.financeiro.ticket_medio) : null
        }
      };

      if (editingClient) {
        await api.put(`/clients/${editingClient.id}`, payload);
        toast.success('Cliente atualizado com sucesso!');
      } else {
        await api.post('/clients', payload);
        toast.success('Cliente criado com sucesso!');
      }
      setIsDialogOpen(false);
      setEditingClient(null);
      resetForm();
      refreshClients();

    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else if (Array.isArray(errorMsg)) {
        toast.error(`Erro de preenchimento: verifique os campos em todas as abas. (${errorMsg[0]?.msg || 'Dados inválidos'})`);
      } else {
        toast.error(editingClient ? 'Erro ao atualizar cliente' : 'Erro ao criar cliente');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      status: 'ativo',
      contact: { email_principal: '', email_financeiro: '', phone_principal: '', phone_adicional: '', whatsapp: '', website: '', instagram: '' },
      juridico: { razao_social: '', nome_fantasia: '', cnpj: '', inscricao_estadual: '', inscricao_municipal: '', tax_regime: '' },
      financeiro: { dia_vencimento: 10, payment_method: '', ticket_medio: '' },
      address: { zipcode: '', street: '', number: '', complement: '', neighborhood: '', city: '', state: '', country: 'Brasil' },
      origem: '',
      segmento: '',
      notes: ''
    });
    setActiveTab('geral');
    setEditingClient(null);
  };

  const openEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name || '',
      status: client.status || 'ativo',
      contact: {
        email_principal: client.contact?.email_principal || '',
        email_financeiro: client.contact?.email_financeiro || '',
        phone_principal: client.contact?.phone_principal || '',
        phone_adicional: client.contact?.phone_adicional || '',
        whatsapp: client.contact?.whatsapp || '',
        website: client.contact?.website || '',
        instagram: client.contact?.instagram || ''
      },
      juridico: {
        razao_social: client.juridico?.razao_social || '',
        nome_fantasia: client.juridico?.nome_fantasia || '',
        cnpj: client.juridico?.cnpj || '',
        inscricao_estadual: client.juridico?.inscricao_estadual || '',
        inscricao_municipal: client.juridico?.inscricao_municipal || '',
        tax_regime: client.juridico?.tax_regime || ''
      },
      financeiro: {
        dia_vencimento: client.financeiro?.dia_vencimento || 10,
        payment_method: client.financeiro?.payment_method || '',
        ticket_medio: client.financeiro?.ticket_medio ? String(client.financeiro.ticket_medio) : ''
      },
      address: {
        zipcode: client.address?.zipcode || '',
        street: client.address?.street || '',
        number: client.address?.number || '',
        complement: client.address?.complement || '',
        neighborhood: client.address?.neighborhood || '',
        city: client.address?.city || '',
        state: client.address?.state || '',
        country: client.address?.country || 'Brasil'
      },
      origem: client.origem || '',
      segmento: client.segmento || '',
      notes: client.notes || ''
    });
    setActiveTab('geral');
    setIsDialogOpen(true);
  };

  const updateContact = (field, value) => {
    setFormData(prev => ({ ...prev, contact: { ...prev.contact, [field]: value } }));
  };

  const updateJuridico = (field, value) => {
    setFormData(prev => ({ ...prev, juridico: { ...prev.juridico, [field]: value } }));
  };

  const updateFinanceiro = (field, value) => {
    setFormData(prev => ({ ...prev, financeiro: { ...prev.financeiro, [field]: value } }));
  };

  const updateAddress = (field, value) => {
    setFormData(prev => ({ ...prev, address: { ...prev.address, [field]: value } }));
  };

  return (
    <div className="space-y-6" data-testid="clientes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight text-slate-900">Clientes</h1>
          <p className="text-slate-600 mt-1">Hub central de gestão de clientes</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90" data-testid="add-client-btn">
              <Plus className="h-4 w-4 mr-2" />
              Novo Cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingClient ? 'Editar Cliente' : 'Novo Cliente'}</DialogTitle>
              <DialogDescription>{editingClient ? 'Atualize as informações do cliente' : 'Preencha os dados para cadastrar um novo cliente'}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-5 mb-6">
                  <TabsTrigger value="geral">Dados Gerais</TabsTrigger>
                  <TabsTrigger value="juridico">Jurídico</TabsTrigger>
                  <TabsTrigger value="financeiro">Financeiro</TabsTrigger>
                  <TabsTrigger value="endereco">Endereço</TabsTrigger>
                  <TabsTrigger value="observacoes">Observações</TabsTrigger>
                </TabsList>

                <TabsContent value="geral" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Nome/Razão Social *</Label>
                      <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="client-name-input" />
                    </div>
                    <div>
                      <Label>Status</Label>
                      <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ativo">Ativo</SelectItem>
                          <SelectItem value="inativo">Inativo</SelectItem>
                          <SelectItem value="potencial">Potencial</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-medium mb-3 flex items-center gap-2"><Mail className="h-4 w-4" /> Contato</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Email Principal</Label>
                        <Input type="email" value={formData.contact.email_principal} onChange={(e) => updateContact('email_principal', e.target.value)} />
                      </div>
                      <div>
                        <Label>Email Financeiro</Label>
                        <Input type="email" value={formData.contact.email_financeiro} onChange={(e) => updateContact('email_financeiro', e.target.value)} />
                      </div>
                      <div>
                        <Label>Telefone Principal</Label>
                        <Input value={formData.contact.phone_principal} onChange={(e) => updateContact('phone_principal', e.target.value)} placeholder="(00) 0000-0000" />
                      </div>
                      <div>
                        <Label>WhatsApp</Label>
                        <Input value={formData.contact.whatsapp} onChange={(e) => updateContact('whatsapp', e.target.value)} placeholder="(00) 00000-0000" />
                      </div>
                      <div>
                        <Label>Website</Label>
                        <Input value={formData.contact.website} onChange={(e) => updateContact('website', e.target.value)} placeholder="https://" />
                      </div>
                      <div>
                        <Label>Instagram</Label>
                        <Input value={formData.contact.instagram} onChange={(e) => updateContact('instagram', e.target.value)} placeholder="@usuario" />
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-medium mb-3">Dados Estratégicos</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Origem</Label>
                        <Select value={formData.origem} onValueChange={(v) => setFormData({ ...formData, origem: v })}>
                          <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                          <SelectContent>
                            {ORIGENS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Segmento</Label>
                        <Select value={formData.segmento} onValueChange={(v) => setFormData({ ...formData, segmento: v })}>
                          <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                          <SelectContent>
                            {SEGMENTOS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="juridico" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Razão Social</Label>
                      <Input value={formData.juridico.razao_social} onChange={(e) => updateJuridico('razao_social', e.target.value)} />
                    </div>
                    <div>
                      <Label>Nome Fantasia</Label>
                      <Input value={formData.juridico.nome_fantasia} onChange={(e) => updateJuridico('nome_fantasia', e.target.value)} />
                    </div>
                    <div>
                      <Label>CNPJ</Label>
                      <Input value={formData.juridico.cnpj} onChange={(e) => updateJuridico('cnpj', e.target.value)} placeholder="00.000.000/0000-00" />
                    </div>
                    <div>
                      <Label>Regime Tributário</Label>
                      <Select value={formData.juridico.tax_regime} onValueChange={(v) => updateJuridico('tax_regime', v)}>
                        <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="simples_nacional">Simples Nacional</SelectItem>
                          <SelectItem value="lucro_presumido">Lucro Presumido</SelectItem>
                          <SelectItem value="lucro_real">Lucro Real</SelectItem>
                          <SelectItem value="mei">MEI</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Inscrição Estadual</Label>
                      <Input value={formData.juridico.inscricao_estadual} onChange={(e) => updateJuridico('inscricao_estadual', e.target.value)} />
                    </div>
                    <div>
                      <Label>Inscrição Municipal</Label>
                      <Input value={formData.juridico.inscricao_municipal} onChange={(e) => updateJuridico('inscricao_municipal', e.target.value)} />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="financeiro" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Dia de Vencimento Padrão</Label>
                      <Input type="number" min="1" max="28" value={formData.financeiro.dia_vencimento} onChange={(e) => updateFinanceiro('dia_vencimento', parseInt(e.target.value))} />
                    </div>
                    <div>
                      <Label>Forma de Pagamento Padrão</Label>
                      <Select value={formData.financeiro.payment_method} onValueChange={(v) => updateFinanceiro('payment_method', v)}>
                        <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pix">PIX</SelectItem>
                          <SelectItem value="boleto">Boleto</SelectItem>
                          <SelectItem value="cartao">Cartão</SelectItem>
                          <SelectItem value="transferencia">Transferência</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Ticket Médio (R$)</Label>
                      <Input type="number" step="0.01" value={formData.financeiro.ticket_medio} onChange={(e) => updateFinanceiro('ticket_medio', e.target.value)} />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="endereco" className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>CEP</Label>
                      <Input value={formData.address.zipcode} onChange={(e) => updateAddress('zipcode', e.target.value)} placeholder="00000-000" />
                    </div>
                    <div className="col-span-2">
                      <Label>Rua/Logradouro</Label>
                      <Input value={formData.address.street} onChange={(e) => updateAddress('street', e.target.value)} />
                    </div>
                    <div>
                      <Label>Número</Label>
                      <Input value={formData.address.number} onChange={(e) => updateAddress('number', e.target.value)} />
                    </div>
                    <div>
                      <Label>Complemento</Label>
                      <Input value={formData.address.complement} onChange={(e) => updateAddress('complement', e.target.value)} />
                    </div>
                    <div>
                      <Label>Bairro</Label>
                      <Input value={formData.address.neighborhood} onChange={(e) => updateAddress('neighborhood', e.target.value)} />
                    </div>
                    <div>
                      <Label>Cidade</Label>
                      <Input value={formData.address.city} onChange={(e) => updateAddress('city', e.target.value)} />
                    </div>
                    <div>
                      <Label>Estado</Label>
                      <Select value={formData.address.state} onValueChange={(v) => updateAddress('state', v)}>
                        <SelectTrigger><SelectValue placeholder="UF" /></SelectTrigger>
                        <SelectContent>
                          {ESTADOS.map(uf => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>País</Label>
                      <Input value={formData.address.country} onChange={(e) => updateAddress('country', e.target.value)} />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="observacoes" className="space-y-4">
                  <div>
                    <Label>Observações Internas</Label>
                    <textarea
                      className="w-full min-h-[200px] px-3 py-2 text-sm rounded-md border border-input bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Anotações internas sobre o cliente..."
                    />
                  </div>
                </TabsContent>
              </Tabs>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
                <Button type="submit" disabled={loading} data-testid="submit-client-btn">
                  {loading ? 'Salvando...' : (editingClient ? 'Atualizar Cliente' : 'Salvar Cliente')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filtros */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
          <Input
            placeholder="Buscar por nome, email, CNPJ..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-clients-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            <SelectItem value="ativo">Ativos</SelectItem>
            <SelectItem value="inativo">Inativos</SelectItem>
            <SelectItem value="potencial">Potenciais</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Cards de Clientes */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredClients.length === 0 ? (
          <div className="col-span-full bg-white p-12 rounded-lg border border-slate-100 text-center">
            <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Nenhum cliente encontrado</p>
          </div>
        ) : (
          filteredClients.map((client) => (
            <div
              key={client.id}
              className="bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-all cursor-pointer group"
              onClick={() => navigate(`/clientes/${client.id}`)}
              data-testid={`client-card-${client.id}`}
            >
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900 group-hover:text-primary transition-colors">{client.name}</h3>
                    {client.juridico?.nome_fantasia && (
                      <p className="text-sm text-slate-500">{client.juridico.nome_fantasia}</p>
                    )}
                  </div>
                  <Badge className={STATUS_CONFIG[client.status]?.color}>
                    {STATUS_CONFIG[client.status]?.label}
                  </Badge>
                </div>

                <div className="space-y-2 text-sm text-slate-600">
                  {client.contact?.email_principal && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-slate-400" />
                      <span className="truncate">{client.contact.email_principal}</span>
                    </div>
                  )}
                  {client.contact?.phone_principal && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-slate-400" />
                      <span>{client.contact.phone_principal}</span>
                    </div>
                  )}
                  {client.segmento && (
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-slate-400" />
                      <span>{client.segmento}</span>
                    </div>
                  )}
                </div>

                {client.financeiro?.ticket_medio && (
                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                    <span className="text-xs text-slate-500 uppercase">Ticket Médio</span>
                    <span className="font-bold text-primary">{formatCurrency(client.financeiro.ticket_medio)}</span>
                  </div>
                )}
              </div>

              <div className="border-t border-slate-100 p-3 bg-slate-50/50 flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEdit(client); }}>
                  <Edit className="h-4 w-4 mr-1" /> Editar
                </Button>
                <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/clientes/${client.id}`); }}>
                  <Eye className="h-4 w-4 mr-1" /> Ver Detalhes
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
